import React, { useEffect, useState } from "react";
import axios from "axios";
import Dialog from "rc-dialog";
import { Link } from "react-router";
import useSWR, { mutate as globalMutate } from "swr";
import Overview, { fetcher } from "./Overview/Overview";
import { useDispatch, useSelector } from "react-redux";
import { snoozeNews } from "../actions/LatestNews";
import Quickview from "./Quickview";
import { NewsOverviewDefinitionBase } from "./Overview/Definitions/NewsOverview";

// confirms the dialog (should call server to ack the message)
const confirmMessage = async (id, callback) => {
    // post to the server
    await axios.put(`/v1/messages/${id}/`, {
        confirm: true
    });

    // Fixme: Temp so you can close the popup :)
    callback();
};

// Dialog which shows news (or changelog)
function NewsDialog({ newsLinkEnabled = true, ...props }) {
    const [message, setMessage] = useState(props.message);
    const [messages, setMessages] = useState(props.messages);
    const [read, setRead] = useState(props?.read);
    const [visible, setVisible] = useState(true);

    const dispatch = useDispatch();

    const unreadMessages = messages?.filter((o) => !o.read) || [];
    const walkMessages = read === true ? messages : unreadMessages;

    const messageIdx = walkMessages.findIndex((o) => o.id === message.id);
    const nextMessage =
        messageIdx >= 0
            ? walkMessages?.[messageIdx + 1]
                ? walkMessages[messageIdx + 1]
                : false
            : walkMessages.length > 0
            ? walkMessages[walkMessages.length - 1]
            : false;
    const prevMessage =
        messageIdx >= 0
            ? walkMessages?.[messageIdx - 1]
                ? walkMessages[messageIdx - 1]
                : false
            : walkMessages.length > 0
            ? walkMessages[0]?.id !== message.id
                ? walkMessages[0]
                : false
            : false;

    const snooze = () => {
        const currentDate = new Date();
        const snoozeUntil = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000);
        dispatch(snoozeNews(snoozeUntil));
    };

    // if we initially set the component to also show 'read' items, we must take it into account
    useEffect(() => {
        if (message.read === true) {
            setRead(true);
        }
    }, [message.read]);

    // Reacts way of allowing html content from the server to be injected
    const newsContent = (item) => {
        return {
            __html: item
        };
    };

    // We can dismiss and defer to a next item, or we can just close the window
    function dismiss(force = false) {
        if (!force) {
            if (nextMessage) {
                setMessage(nextMessage);
            } else {
                setVisible(false);
                props.callback();
            }
        } else {
            setVisible(false);
            props.callback();
        }
    }

    return (
        <Dialog
            title={message.title}
            onClose={() => dismiss(true)}
            visible={visible}
            animation="fade"
            footer={[
                <button type={"button"} className={"btn btn-secondary"} disabled={!prevMessage} key={"prev" + prevMessage.id} onClick={() => setMessage(prevMessage)}>
                    &lt;
                </button>,
                <button type={"button"} className={"btn btn-secondary"} disabled={!nextMessage} key={"next" + nextMessage.id} onClick={() => setMessage(nextMessage)}>
                    &gt;
                </button>,
                newsLinkEnabled ? (
                    <span className="rc-dialog-footer__text" key="text">
                        Bekijk het{" "}
                        <Link to={"news"} onClick={dismiss}>
                            nieuwsarchief
                        </Link>
                    </span>
                ) : (
                    <span className="rc-dialog-footer__text" key="text" />
                ),
                !message.read && (
                    <button
                        type="button"
                        className="btn btn-secondary"
                        key="no"
                        onClick={() => {
                            if (newsLinkEnabled) {
                                snooze();
                            }
                            dismiss();
                        }}
                    >
                        {newsLinkEnabled ? <>Herinner me later</> : <>Sluit</>}
                    </button>
                ),
                <button type="button" className="btn btn-primary" key="yes" onClick={() => (message.read ? dismiss() : confirmMessage(message?.id, dismiss))}>
                    {message.read ? "Sluit" : "Markeer als gelezen"}
                </button>
            ]}
        >
            <div dangerouslySetInnerHTML={newsContent(message.body)} />
            {message?.external_url && (
                <a target={"_blank"} href={message.external_url}>
                    {message.external_url}
                </a>
            )}
        </Dialog>
    );
}

// Fetch the latest news
export function LatestNewsFetcher(props) {
    const { data: newMessages, mutate } = useSWR(() => `/v1/messages/?ordering=-publication_date&application=portaal.nibhv.nl`, fetcher);
    const messages = newMessages?.results.filter((o) => o.read === false) || [];
    const message = messages.length >= 0 ? messages[0] : false;
    const date = new Date();

    let state = useSelector((state) => state?.LatestNewsState);

    if (!(messages?.length > 0) || new Date(state.time) > date || typeof state?.time === "undefined") {
        return null; // do nothing when no messages present or when we snoozed
    }

    return message ? <NewsDialog message={message} messages={messages} callback={() => mutate()} /> : null;
}

// Display a listing of news items (as an ordered list)
export function NewsList(props) {
    const [activeMessage, setActiveMessage] = useState(false);
    const { data: newMessages, mutate } = useSWR(() => `/v1/messages/?ordering=-publication_date&application=portaal.nibhv.nl`, fetcher);
    const messages = newMessages?.results || [];

    return (
        <React.Fragment>
            {activeMessage && (
                <NewsDialog
                    newsLinkEnabled={false}
                    message={activeMessage}
                    messages={messages}
                    callback={() => {
                        mutate();
                        setActiveMessage(false);
                    }}
                />
            )}

            <Quickview>
                <Overview
                    quickview={{
                        identifier: "news",
                        name: "Nieuwsberichten"
                    }}
                    identifier={"news-overview"}
                    definition={NewsOverviewDefinitionBase}
                    contextData={{
                        setActiveMessage
                    }}
                />
            </Quickview>
        </React.Fragment>
    );
}
